import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaSearch } from 'react-icons/fa';
import { MdDocumentScanner } from 'react-icons/md';
import Sidebar from './Sidebar';
import { useMerchantAuth } from '../../../contexts/AuthContext';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { useAppDispatch } from '../../store/hooks';
import { setMode } from '../../../features/scan/store/scanSlice';
import { useSelector } from 'react-redux';
import { createLogger } from '../../config/logger';
const logger = createLogger('Header.jsx');

export default function Header() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { loyalpassAdmin } = useMerchantAuth();
    const dispatch = useAppDispatch();
    const merchantName = useSelector((state) => state.settings.generalsettings?.name || '');

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const handleLogoutConfirm = async () => {
        setShowLogoutConfirm(false);
        setIsSidebarOpen(false);
        try {
            logger.info('Logout confirmed');
            await logout();
            logger.info('User logged out');
        } catch (err) {
            logger.error('Unexpected error during logout', { error: err });
            alert('An error occurred during logout.');
        }
    };

    return (
        <>
            {/* Header */}
            <header className="w-full bg-card px-4 sm:px-6 py-4 shadow-sm flex items-center">
                {/* Left: hamburger + store name */}
                <div className="flex items-center gap-4 min-w-0 flex-1">
                    <button onClick={() => setIsSidebarOpen(true)} className="md:hidden shrink-0 text-white">
                        <FaBars size={20} />
                    </button>
                    {loyalpassAdmin ? (
                        <span className="sr-only">LoyalPass platform admin</span>
                    ) : (
                        <h2 className="text-xl font-semibold text-cta truncate">
                            <span className="md:hidden text-lg font-bold text-[#00FFD0]">
                                {merchantName || 'Store'}
                            </span>
                            <span className="hidden md:inline">
                                {merchantName ? (
                                    <span className="text-2xl font-bold text-[#00FFD0]">
                                        {merchantName}'s <span className="text-[#FF4DA6]">Store 🌴</span>
                                    </span>
                                ) : (
                                    'Store'
                                )}
                            </span>
                        </h2>
                    )}
                </div>

                {/* Center: scan + search (merchant dashboard only — not for LoyalPass platform admins) */}
                {!loyalpassAdmin && (
                    <div className="flex justify-center items-center gap-4 shrink-0">
                        <button
                            onClick={() => {
                                dispatch(setMode('qr'));
                                navigate('/dashboard/scan');
                            }}
                            className="text-primary hover:text-primary-dark focus:outline-none"
                            title="Scan Pass"
                        >
                            <MdDocumentScanner size={22} />
                        </button>
                        <button
                            onClick={() => {
                                dispatch(setMode('email'));
                                navigate('/dashboard/scan');
                            }}
                            className="text-primary hover:text-primary-dark focus:outline-none"
                            title="Search for customer"
                        >
                            <FaSearch size={22} />
                        </button>
                    </div>
                )}

                {/* Right: logout (desktop only; mobile has logout in hamburger menu) */}
                <div className="flex flex-1 justify-end items-center shrink-0">
                    <button
                        onClick={handleLogoutClick}
                        className="hidden md:block bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm transition"
                    >
                        Logout
                    </button>
                </div>
            </header>

            {/* Mobile Sidebar Drawer */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-40 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                >
                    <div
                        className="absolute top-0 left-0 h-full w-64 bg-card shadow-lg flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-4 border-b shrink-0">
                            <span className="text-2xl font-bold text-[#00FFD0]">
                                Loyal<span className="text-[#FF4DA6]">Pass</span>
                            </span>
                            <button className="text-white" onClick={() => setIsSidebarOpen(false)}>
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto">
                            <Sidebar isMobile onNavigate={() => setIsSidebarOpen(false)} />
                        </div>
                        <div className="p-4 border-t shrink-0">
                            <button
                                onClick={handleLogoutClick}
                                className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white font-medium transition"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Logout confirmation modal */}
            {showLogoutConfirm && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4"
                    onClick={() => setShowLogoutConfirm(false)}
                >
                    <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Log out?</h3>
                        <p className="text-gray-600 text-sm mb-6">Are you sure you want to log out?</p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setShowLogoutConfirm(false)}
                                className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleLogoutConfirm}
                                className="flex-1 py-2 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium"
                            >
                                Log out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
